import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  UserGroupIcon, 
  PlusIcon, 
  ShareIcon, 
  TrophyIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
// import api from '../utils/api' // TODO: Update for new API structure

const FamilySync = ({ userId, onClose }) => {
  const [families, setFamilies] = useState([])
  const [activeFamily, setActiveFamily] = useState(null)
  const [showJoinForm, setShowJoinForm] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')

  useEffect(() => {
    loadUserFamilies()
  }, [userId])

  const loadUserFamilies = async () => {
    try {
      const response = await api.get(`/users/${userId}/families`)
      setFamilies(response.data)
      if (response.data.length > 0 && !activeFamily) {
        setActiveFamily(response.data[0])
      }
    } catch (error) {
      console.error('Error loading families:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="text-center mt-4 text-gray-600">Loading families...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <UserGroupIcon className="h-8 w-8" />
              <div>
                <h2 className="text-2xl font-bold">Family Sync</h2>
                <p className="opacity-90">Connect and compete with family</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex h-[calc(90vh-140px)]">
          {/* Sidebar */}
          <div className="w-80 border-r bg-gray-50 p-4 overflow-y-auto">
            {families.length === 0 ? (
              <div className="text-center py-8">
                <UserGroupIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No families yet</p>
                <div className="space-y-2">
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="w-full bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 flex items-center justify-center gap-2"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Create Family
                  </button>
                  <button
                    onClick={() => setShowJoinForm(true)}
                    className="w-full border border-orange-500 text-orange-500 px-4 py-2 rounded-lg hover:bg-orange-50 flex items-center justify-center gap-2"
                  >
                    <ShareIcon className="h-4 w-4" />
                    Join Family
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="flex-1 bg-orange-500 text-white px-3 py-2 rounded-lg hover:bg-orange-600 flex items-center justify-center gap-2 text-sm"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Create
                  </button>
                  <button
                    onClick={() => setShowJoinForm(true)}
                    className="flex-1 border border-orange-500 text-orange-500 px-3 py-2 rounded-lg hover:bg-orange-50 flex items-center justify-center gap-2 text-sm"
                  >
                    <ShareIcon className="h-4 w-4" />
                    Join
                  </button>
                </div>

                {families.map((family) => (
                  <div
                    key={family.id}
                    onClick={() => setActiveFamily(family)}
                    className={`p-4 rounded-xl cursor-pointer transition-all ${
                      activeFamily?.id === family.id
                        ? 'bg-orange-100 border-2 border-orange-300'
                        : 'bg-white border hover:bg-gray-50'
                    }`}
                  >
                    <h3 className="font-semibold text-gray-900">{family.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{family.member_count} members</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        family.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                        family.role === 'moderator' ? 'bg-blue-100 text-blue-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {family.role}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col">
            {activeFamily ? (
              <>
                {/* Tabs */}
                <div className="border-b">
                  <nav className="flex">
                    {[
                      { id: 'overview', name: 'Overview', icon: ChartBarIcon },
                      { id: 'challenges', name: 'Challenges', icon: TrophyIcon },
                      { id: 'leaderboard', name: 'Leaderboard', icon: ChartBarIcon },
                      { id: 'feed', name: 'Family Feed', icon: ShareIcon }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm ${
                          activeTab === tab.id
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <tab.icon className="h-4 w-4" />
                        {tab.name}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {activeTab === 'overview' && (
                    <FamilyOverview family={activeFamily} />
                  )}
                  {activeTab === 'challenges' && (
                    <FamilyChallenges family={activeFamily} userId={userId} />
                  )}
                  {activeTab === 'leaderboard' && (
                    <FamilyLeaderboard family={activeFamily} userId={userId} />
                  )}
                  {activeTab === 'feed' && (
                    <FamilyFeed family={activeFamily} userId={userId} />
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <UserGroupIcon className="h-24 w-24 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">Select a family to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        <AnimatePresence>
          {showCreateForm && (
            <CreateFamilyModal
              onClose={() => setShowCreateForm(false)}
              onSuccess={() => {
                setShowCreateForm(false)
                loadUserFamilies()
              }}
            />
          )}
          {showJoinForm && (
            <JoinFamilyModal
              onClose={() => setShowJoinForm(false)}
              onSuccess={() => {
                setShowJoinForm(false)
                loadUserFamilies()
              }}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  )
}

// Sub-components
const FamilyOverview = ({ family }) => {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadFamilyMembers()
  }, [family.id])

  const loadFamilyMembers = async () => {
    try {
      const response = await api.get(`/families/${family.id}/members`)
      setMembers(response.data)
    } catch (error) {
      console.error('Error loading family members:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div className="animate-pulse">Loading...</div>
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-gray-900">{family.name}</h3>
        {family.description && (
          <p className="text-gray-600 mt-2">{family.description}</p>
        )}
        <div className="flex items-center gap-4 mt-4">
          <span className="text-sm text-gray-500">
            Invite Code: <span className="font-mono font-bold text-orange-600">{family.invite_code}</span>
          </span>
          <button
            onClick={() => navigator.clipboard.writeText(family.invite_code)}
            className="text-sm text-orange-600 hover:text-orange-700"
          >
            Copy
          </button>
        </div>
      </div>

      <div>
        <h4 className="text-lg font-semibold mb-4">Family Members ({members.length})</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((member, index) => (
            <div key={index} className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-semibold">{member.nickname || member.username}</h5>
                  <p className="text-sm text-gray-600">{member.username}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  member.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                  member.role === 'moderator' ? 'bg-blue-100 text-blue-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {member.role}
                </span>
              </div>
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">XP:</span>
                  <span className="font-medium">{member.total_xp || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Streak:</span>
                  <span className="font-medium">{member.current_streak || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Lessons:</span>
                  <span className="font-medium">{member.lessons_completed_count || 0}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const FamilyChallenges = ({ family, userId }) => {
  const [challenges, setChallenges] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadChallenges()
  }, [family.id])

  const loadChallenges = async () => {
    try {
      const response = await api.get(`/families/${family.id}/challenges`)
      setChallenges(response.data)
    } catch (error) {
      console.error('Error loading challenges:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div className="animate-pulse">Loading challenges...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-gray-900">Family Challenges</h3>
        <button className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 flex items-center gap-2">
          <PlusIcon className="h-4 w-4" />
          Create Challenge
        </button>
      </div>

      {challenges.length === 0 ? (
        <div className="text-center py-12">
          <TrophyIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No challenges yet</p>
          <p className="text-sm text-gray-500">Create your first family challenge to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {challenges.map((challenge) => (
            <div key={challenge.id} className="bg-white border rounded-xl p-6 shadow-sm">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-lg">{challenge.name}</h4>
                  <p className="text-gray-600 text-sm mt-1">{challenge.description}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  challenge.completed_at ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {challenge.completed_at ? 'Completed' : 'Active'}
                </span>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Progress:</span>
                  <span className="font-medium">
                    {challenge.current_progress || 0} / {challenge.target_value}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-orange-500 h-2 rounded-full" 
                    style={{ 
                      width: `${Math.min(((challenge.current_progress || 0) / challenge.target_value) * 100, 100)}%` 
                    }}
                  />
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Participants: {challenge.participant_count}</span>
                  <span>Completed: {challenge.completed_count}</span>
                </div>
                {challenge.end_date && (
                  <p className="text-sm text-gray-500">
                    Ends: {new Date(challenge.end_date).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const FamilyLeaderboard = ({ family, userId }) => {
  const [leaderboard, setLeaderboard] = useState([])
  const [loading, setLoading] = useState(true)
  const [metric, setMetric] = useState('total_xp')

  useEffect(() => {
    loadLeaderboard()
  }, [family.id, metric])

  const loadLeaderboard = async () => {
    try {
      const response = await api.get(`/families/${family.id}/leaderboard?metric=${metric}`)
      setLeaderboard(response.data)
    } catch (error) {
      console.error('Error loading leaderboard:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div className="animate-pulse">Loading leaderboard...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-gray-900">Leaderboard</h3>
        <select
          value={metric}
          onChange={(e) => setMetric(e.target.value)}
          className="border rounded-lg px-3 py-2"
        >
          <option value="total_xp">Total XP</option>
          <option value="current_streak">Current Streak</option>
          <option value="lessons_completed">Lessons Completed</option>
          <option value="badges_earned">Badges Earned</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        {leaderboard.map((member, index) => (
          <div
            key={index}
            className={`flex items-center justify-between p-4 border-b last:border-b-0 ${
              index < 3 ? 'bg-gradient-to-r from-yellow-50 to-orange-50' : ''
            }`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                index === 0 ? 'bg-yellow-500 text-white' :
                index === 1 ? 'bg-gray-400 text-white' :
                index === 2 ? 'bg-orange-600 text-white' :
                'bg-gray-200 text-gray-600'
              }`}>
                {index + 1}
              </div>
              <div>
                <p className="font-semibold">{member.nickname || member.username}</p>
                <p className="text-sm text-gray-600">{member.username}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-bold text-lg">{member[metric] || 0}</p>
              <p className="text-sm text-gray-600 capitalize">{metric.replace('_', ' ')}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const FamilyFeed = ({ family, userId }) => {
  const [feed, setFeed] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadFeed()
  }, [family.id])

  const loadFeed = async () => {
    try {
      const response = await api.get(`/families/${family.id}/feed`)
      setFeed(response.data)
    } catch (error) {
      console.error('Error loading feed:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div className="animate-pulse">Loading feed...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-gray-900">Family Feed</h3>
        <button className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 flex items-center gap-2">
          <ShareIcon className="h-4 w-4" />
          Share Update
        </button>
      </div>

      {feed.length === 0 ? (
        <div className="text-center py-12">
          <ShareIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No updates yet</p>
          <p className="text-sm text-gray-500">Share your progress to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {feed.map((item) => (
            <div key={item.id} className="bg-white border rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-semibold">
                  {item.shared_by_username?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold">{item.shared_by_username}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.content_type === 'achievement' ? 'bg-yellow-100 text-yellow-700' :
                      item.content_type === 'tip' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {item.content_type}
                    </span>
                    <span className="text-sm text-gray-500">
                      {new Date(item.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <h4 className="font-medium mb-2">{item.title}</h4>
                  <p className="text-gray-700 mb-3">{item.content}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <button className="hover:text-orange-600">
                      ❤️ {item.likes_count || 0}
                    </button>
                    <button className="hover:text-orange-600">
                      💬 {item.comments_count || 0}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Modal Components
const CreateFamilyModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    maxMembers: 8
  })
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      await api.post('/families', formData)
      onSuccess()
    } catch (error) {
      console.error('Error creating family:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-60">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-2xl p-6 max-w-md w-full mx-4"
      >
        <h3 className="text-xl font-bold mb-4">Create Family</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Family Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Description (Optional)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 h-20 resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Max Members</label>
            <select
              value={formData.maxMembers}
              onChange={(e) => setFormData({ ...formData, maxMembers: parseInt(e.target.value) })}
              className="w-full border rounded-lg px-3 py-2"
            >
              {[2, 4, 6, 8, 10, 12, 15, 20].map(num => (
                <option key={num} value={num}>{num} members</option>
              ))}
            </select>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Family'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

const JoinFamilyModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    inviteCode: '',
    nickname: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    
    try {
      await api.post('/families/join', formData)
      onSuccess()
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to join family')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-60">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-2xl p-6 max-w-md w-full mx-4"
      >
        <h3 className="text-xl font-bold mb-4">Join Family</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Invite Code</label>
            <input
              type="text"
              value={formData.inviteCode}
              onChange={(e) => setFormData({ ...formData, inviteCode: e.target.value.toUpperCase() })}
              className="w-full border rounded-lg px-3 py-2 font-mono"
              placeholder="FAM123456"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Nickname (Optional)</label>
            <input
              type="text"
              value={formData.nickname}
              onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              placeholder="How you want to appear to family"
            />
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 disabled:opacity-50"
            >
              {loading ? 'Joining...' : 'Join Family'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

export default FamilySync